import { Box, Button, makeStyles, TextField } from '@material-ui/core';
import React, { useRef } from 'react';
// TODO: update icon
import LoyaltyIcon from '@material-ui/icons/Loyalty';
import { useInputState } from 'lib/useInputState';
// TODO: update icon
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import { BottomDialog } from 'lib/BottomDialog';
import { Html5Qrcode } from 'html5-qrcode';
import { useTranslation } from 'react-i18next';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { useSnackbar } from 'notistack';

const READER_ELEMENT_ID = 'qr-code-reader';

const useStyles = makeStyles(theme => ({
    button: {
        marginLeft: theme.spacing(1)
    }
}));

interface CouponInputProps {
    onAdd: (coupon: string) => void;
}

// TODO: import qrcode library dynamicly
export const CouponInput: React.FC<CouponInputProps> = ({ onAdd }) => {
    const [value, onInputChange, onValueChange] = useInputState();
    const [open, setOpen] = React.useState(false);
    const { t } = useTranslation();
    const classes = useStyles();
    const html5QrCode = useRef<Html5Qrcode>();
    const { enqueueSnackbar } = useSnackbar();
    const { logUserEvent } = useGAHelpers();
    const handleClick = React.useCallback(() => {
        onAdd(value);
        onValueChange();
        logUserEvent(EGAEventName.CheckoutCouponSubmitted);
    }, [logUserEvent, onAdd, onValueChange, value]);
    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent) => {
            if (e.keyCode === 13) {
                handleClick();
            }
        },
        [handleClick]
    );
    const handleCameraClick = React.useCallback(() => {
        setOpen(true);
        logUserEvent(EGAEventName.CheckoutCouponCameraStarted);
    }, [logUserEvent]);
    const handleDialogClose = React.useCallback(async () => {
        if (!html5QrCode.current) {
            html5QrCode.current = new Html5Qrcode(READER_ELEMENT_ID);
        }
        try {
            await html5QrCode.current?.stop();
            setOpen(false);
        } catch {
            setOpen(false);
        }
    }, []);
    const onScanSuccess = React.useCallback(
        (decodedText: string) => {
            logUserEvent(EGAEventName.CheckoutCouponCameraSubmitted);
            onAdd(decodedText);
            onValueChange();
            handleDialogClose();
        },
        [handleDialogClose, logUserEvent, onAdd, onValueChange]
    );
    const handleTransitionEnd = React.useCallback(() => {
        if (open) {
            if (!html5QrCode.current) {
                html5QrCode.current = new Html5Qrcode(READER_ELEMENT_ID);
            }
            Html5Qrcode?.getCameras()
                .then(devices => {
                    const config = { fps: 10, qrbox: { width: 250, height: 250 } };
                    if (devices && devices.length) {
                        html5QrCode.current?.start(
                            { facingMode: 'environment' },
                            config,
                            onScanSuccess,
                            () => {
                                // this callback is fired when no qr code is present in the camera frame
                            }
                        );
                    }
                })
                .catch(() => {
                    enqueueSnackbar(t('CHECKOUT_COUPON_CAMERA_ERROR'), { variant: 'error' });
                    handleDialogClose();
                });
        }
    }, [enqueueSnackbar, handleDialogClose, onScanSuccess, open, t]);
    const handleInputClick = React.useCallback(() => {
        logUserEvent(EGAEventName.CheckoutCouponEntryStarted);
    }, [logUserEvent]);
    return (
        <Box flex={1} display="flex">
            <TextField
                InputProps={{
                    startAdornment: <LoyaltyIcon />,
                    endAdornment: <CameraAltIcon onClick={handleCameraClick} />
                }}
                onClick={handleInputClick}
                onChange={onInputChange}
                value={value}
                label={t('CHECKOUT_COUPON_ENTRY_TEXT')}
                variant="outlined"
                size="small"
                fullWidth
                onKeyDown={handleKeyDown}
            />
            <Button
                disabled={!value?.length}
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={handleClick}
            >
                {t('CHECKOUT_COUPON_APPLY_BTN')}
            </Button>
            <BottomDialog open={open} onClose={handleDialogClose} onEntered={handleTransitionEnd}>
                <Box id={READER_ELEMENT_ID} height="500px" width="100%"></Box>
            </BottomDialog>
        </Box>
    );
};
